import { Permissions, OrganisationProducts } from "@/constants/access";
import type { NavContext, NavItem } from "@/types/routes";
import {
    mdiCircleMedium,
    mdiCreditCardOutline,
    mdiFilePlusOutline,
    mdiFolderOutline,
    mdiHelp,
    mdiHome,
    mdiInformationOutline,
    mdiLogout,
    mdiSwapHorizontal,
    mdiTrendingUp,
    mdiUpload,
    mdiWalletOutline,
} from "@mdi/js";

export const navSchoolRoutes: NavItem[] = [
    {
        id: "dashboard",
        title: "Dashboard",
        iconPath: mdiHome,
        route: "/school/overview",
        requiredPermissions: [],
        requiredOrganisationProducts: [],
    },
    {
        id: "transactions",
        title: "Transactions",
        iconPath: mdiSwapHorizontal,
        route: "/school/transactions",
        requiredPermissions: [],
        requiredOrganisationProducts: [],
        subitems: [
            {
                id: "transactions:payments",
                title: "Payments",
                iconPath: mdiCircleMedium,
                route: "/school/transactions/payments",
                requiredPermissions: [],
                requiredOrganisationProducts: [],
            },
            {
                id: "transactions:unrecognised",
                title: "Unrecognised Payments",
                iconPath: mdiCircleMedium,
                route: "/school/transactions/unrecognised-payments",
                requiredPermissions: [],
                requiredOrganisationProducts: [],
            },
            {
                id: "transactions:direct-debit",
                title: "Direct Debit",
                iconPath: mdiCircleMedium,
                route: "/school/transactions/direct-debits",
                requiredPermissions: [Permissions.DIRECT_DEBIT],
                requiredOrganisationProducts: [OrganisationProducts.BACS],
            },
            {
                id: "transactions:payroll",
                title: "Direct Credit Payroll",
                iconPath: mdiCircleMedium,
                route: "/school/transactions/direct-credit-payroll",
                requiredPermissions: [Permissions.PAYROLL],
                requiredOrganisationProducts: [OrganisationProducts.BACS],
            },
            {
                id: "transactions:supplier",
                title: "Direct Credit Supplier",
                iconPath: mdiCircleMedium,
                route: "/school/transactions/direct-credit-supplier",
                requiredPermissions: [Permissions.SUPPLIER],
                requiredOrganisationProducts: [OrganisationProducts.BACS],
            },
        ],
    },
    {
        id: "reconciliation",
        title: "Reconciliation",
        iconPath: mdiUpload,
        route: "/school/reconciliation",
        requiredPermissions: [Permissions.RECONCILIATION],
        requiredOrganisationProducts: [],
        subitems: [
            {
                id: "reconciliation:matches",
                title: "Matched Records",
                iconPath: mdiCircleMedium,
                route: "/school/reconciliation/matches",
                requiredPermissions: [Permissions.RECONCILIATION],
                requiredOrganisationProducts: [],
            },
            {
                id: "reconciliation:posting-report",
                title: "Posting Report",
                iconPath: mdiCircleMedium,
                route: "/school/reconciliation/posting-report",
                requiredPermissions: [Permissions.RECONCILIATION],
                requiredOrganisationProducts: [],
            },
            {
                id: "reconciliation:unmatched",
                title: "Unmatched Transactions",
                iconPath: mdiCircleMedium,
                route: "/school/reconciliation/unmatched-and-ignored-transactions",
                requiredPermissions: [Permissions.RECONCILIATION],
                requiredOrganisationProducts: [],
            },
        ],
    },
    {
        id: "payment-links",
        title: "Payment Links",
        iconPath: mdiCreditCardOutline,
        route: "/school/payment-request-links",
        requiredPermissions: [],
        requiredOrganisationProducts: [],
    },
    {
        id: "bacs-actions",
        title: "BACS Actions",
        iconPath: mdiWalletOutline,
        route: "/school/bacs/mandates",
        requiredPermissions: [],
        requiredOrganisationProducts: [OrganisationProducts.BACS],
        subitems: [
            {
                id: "bacs-actions:mandates",
                title: "Mandates",
                iconPath: mdiCircleMedium,
                route: "/school/bacs/mandate/batches",
                requiredPermissions: [Permissions.MANDATE],
                requiredOrganisationProducts: [OrganisationProducts.BACS],
            },
            {
                id: "bacs-actions:direct-debit",
                title: "Direct Debit",
                iconPath: mdiCircleMedium,
                route: "/school/bacs/direct-debit/batches",
                requiredPermissions: [Permissions.DIRECT_DEBIT],
                requiredOrganisationProducts: [OrganisationProducts.BACS],
            },
            {
                id: "bacs-actions:payroll",
                title: "Payroll Payments",
                iconPath: mdiCircleMedium,
                route: "/school/bacs/direct-credit-payroll/batches",
                requiredPermissions: [Permissions.PAYROLL],
                requiredOrganisationProducts: [OrganisationProducts.BACS],
            },
            {
                id: "bacs-actions:supplier",
                title: "Supplier Payments",
                iconPath: mdiCircleMedium,
                route: "/school/bacs/direct-credit-supplier/batches",
                requiredPermissions: [Permissions.SUPPLIER],
                requiredOrganisationProducts: [OrganisationProducts.BACS],
            },
        ],
    },
    {
        id: "bacs-reports",
        title: "BACS Reports",
        iconPath: mdiInformationOutline,
        route: "/school/bacs/reports",
        requiredPermissions: [Permissions.BACS_REPORTS],
        requiredOrganisationProducts: [OrganisationProducts.BACS],
    },
    {
        id: "all-mandates",
        title: "All Mandates",
        iconPath: mdiFolderOutline,
        route: "/school/mandates",
        requiredPermissions: [],
        requiredOrganisationProducts: [OrganisationProducts.BACS],
    },
    {
        id: "card-payouts",
        title: "Card Payouts",
        iconPath: mdiTrendingUp,
        route: "/school/card-payouts",
        requiredPermissions: [Permissions.BASE],
        requiredOrganisationProducts: [OrganisationProducts.CARD_STATEMENTS],
    },
    {
        id: "compliance",
        title: "Compliance",
        iconPath: mdiFilePlusOutline,
        route: "/school/compliance",
        requiredPermissions: [Permissions.ORG_ADMIN],
        requiredOrganisationProducts: [
            OrganisationProducts.ADYEN_ONBOARDING,
            OrganisationProducts.CONTRACTS,
        ], // OrganisationProducts.SOKIN_COMPLIANCE temporarily removed from parent item since it has no active subitems, but left on subitems for future use
        subitems: [
            // !!! Sokin compliance sections are temporarily hidden.
            // {
            //     id: "compliance:company-details",
            //     title: "Company Details",
            //     iconPath: mdiCircleMedium,
            //     route: "/school/compliance/company-details",
            //     requiredPermissions: [Permissions.ORG_ADMIN],
            //     requiredOrganisationProducts: [OrganisationProducts.SOKIN_COMPLIANCE],
            // },
            // ... other hidden items
            {
                id: "compliance:card-onboarding",
                title: "Onboarding",
                iconPath: mdiCircleMedium,
                route: "/school/compliance/card-provider-onboarding",
                requiredPermissions: [Permissions.ORG_ADMIN],
                requiredOrganisationProducts: [
                    OrganisationProducts.ADYEN_ONBOARDING,
                ],
            },
            {
                id: "compliance:contracts",
                title: "Contracts",
                iconPath: mdiCircleMedium,
                route: "/school/compliance/contracts",
                requiredPermissions: [Permissions.ORG_ADMIN],
                requiredOrganisationProducts: [OrganisationProducts.CONTRACTS],
            },
        ],
    },
    {
        id: "support",
        title: "Support",
        iconPath: mdiHelp,
        route: "/school/support",
        requiredPermissions: [],
        requiredOrganisationProducts: [],
    },
    {
        id: "logout",
        title: "Logout",
        iconPath: mdiLogout,
        route: "logout",
        requiredPermissions: [],
        requiredOrganisationProducts: [],
    },
];

// ---------------------------------------------------------------------------
// Filtering logic — pure function, no component dependencies
// ---------------------------------------------------------------------------

// These routes should be the only ones visible when contracts are not yet signed
const CONTRACTS_ONLY_IDS = new Set(["compliance", "support", "logout"]);

/**
 * Returns true when the given item should be visible given the current context.
 * Each check is independent and short-circuits early.
 */
function isItemVisible(item: NavItem, ctx: NavContext): boolean {
    const required = item.requiredOrganisationProducts;

    // Item is hidden if it requires org perms that aren't all present.
    // An item with multiple requiredOrganisationProducts is shown when
    // ANY of them is enabled (e.g. the Compliance parent needs only one sub-feature).
    if (
        required.length > 0 &&
        !required.some((p) => ctx.orgProducts.includes(p))
    )
        return false;

    const perms = item.requiredPermissions;
    return perms.length === 0 || ctx.hasPermission(perms);
}

/**
 * Filters a flat list of routes (and their subitems) based on the given context.
 * Returns a new array — the original navSchoolRoutes is never mutated.
 */
export function filterNavRoutes(routes: NavItem[], ctx: NavContext): NavItem[] {
    // When contracts are not yet signed, only show a small subset of routes
    if (!ctx.contractsSigned) {
        return routes.flatMap((route) => {
            if (!CONTRACTS_ONLY_IDS.has(route.id)) return [];
            if (!isItemVisible(route, ctx)) return [];
            if (!route.subitems) return [route];
            const subitems = route.subitems.filter((s) =>
                isItemVisible(s, ctx),
            );
            return subitems.length > 0
                ? [{ ...route, subitems }]
                : [{ ...route, subitems: undefined }];
        });
    }

    // Otherwise, filter normally based on permissions and products
    return routes.flatMap((route) => {
        if (!isItemVisible(route, ctx)) return [];
        if (!route.subitems) return [route];

        const subitems = route.subitems.filter((s) => isItemVisible(s, ctx));
        if (subitems.length === 0) return [];
        return [{ ...route, subitems }];
    });
}
